import { useEffect, useMemo, useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import {
  ResponsiveContainer, LineChart as RLineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid,
} from "recharts";
import { Play, X, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api, fmtUsd, fmtPct, fmtTime } from "@/lib/api";
import { SectionTitle, StatCard, Badge } from "@/components/Bits";
import { StrategyPicker, StrategySummary } from "@/components/StrategyPicker";
import { RESEARCH_PRESETS, findPreset } from "@/lib/researchPresets";
import { ResearchCachePill } from "@/components/ResearchCachePill";
import { WarmAllPresetsButton } from "@/components/WarmAllPresetsButton";
import { CacheTelemetryBadge } from "@/components/CacheTelemetryBadge";
import { loadResearchPreset, persistResearchPreset } from "@/lib/researchPresetStorage";

const isoToInput = (iso) => {
  const d = iso ? new Date(iso) : new Date();
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};
const defaultStart = () => { const d = new Date(); d.setDate(d.getDate() - 30); return isoToInput(d.toISOString()); };
const defaultEnd = () => isoToInput(new Date().toISOString());

const labelKind = (label) => ({
  Strong: "on",
  Moderate: "warn",
  Weak: "off",
  "Overfit Risk": "short",
}[label] || "neutral");

// --- Persisted form settings ---------------------------------------
// Form values + selected strategy profile id are persisted to
// localStorage under this key so a refresh / re-login restores the
// last config the operator was running. Math, strategy evaluation
// and live trading are untouched — pure UI persistence.
const WF_FORM_STORAGE_KEY = "walkforward_form_settings_v1";

const DEFAULT_FORM = {
  symbol: "BTCUSDT",
  start_iso: defaultStart(),
  end_iso: defaultEnd(),
  n_folds: 5,
  initial_balance: 10000,
  leverage: 5,
  order_size_mode: "usdt",
  order_size_value: 200,
  // Default OFF for LBank one-way parity — see Backtest.jsx for full
  // rationale. Toggle remains visible for advanced research workflows.
  hedge_mode: false,
  fee_rate: 0.0004,
  slippage_bps: 0,
  liquidations_mode: "skip",
};

const PERSISTED_FORM_KEYS = [
  "n_folds", "liquidations_mode", "start_iso", "end_iso",
  "initial_balance", "leverage", "order_size_mode", "order_size_value",
  "fee_rate", "slippage_bps", "hedge_mode",
];

function _loadPersisted() {
  try {
    const raw = localStorage.getItem(WF_FORM_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return null;
    return parsed;
  } catch {
    return null;
  }
}

function _savePersisted(payload) {
  try {
    localStorage.setItem(WF_FORM_STORAGE_KEY, JSON.stringify(payload));
  } catch {
    // Quota or private-browsing: degrade silently.
  }
}

// Active = a WF that hasn't reached a terminal state yet. List
// endpoint must surface enough fields (status + progress_*) for this
// check — see /api/walkforward/list lightweight projection.
const _isActiveStatus = (s) => s === "pending" || s === "running";

// Minutes elapsed since the engine's last persist call. Used to
// surface "possibly stalled" without auto-killing the job (per
// product requirement — operator stays in control).
function _staleMinutes(updatedAtIso) {
  if (!updatedAtIso) return null;
  const t = Date.parse(updatedAtIso);
  if (!Number.isFinite(t)) return null;
  const delta = Date.now() - t;
  return delta < 0 ? 0 : delta / 60000;
}

export default function WalkForward() {
  // Restore persisted form on first render (lazy initializer — runs
  // exactly once, before subscribing to the persistence effect).
  const [form, setForm] = useState(() => {
    const persisted = _loadPersisted();
    if (!persisted) return DEFAULT_FORM;
    const next = { ...DEFAULT_FORM };
    for (const k of PERSISTED_FORM_KEYS) {
      if (persisted[k] !== undefined) next[k] = persisted[k];
    }
    return next;
  });
  const [picker, setPicker] = useState(() => {
    const persisted = _loadPersisted();
    if (persisted && persisted.profileId) {
      return { mode: "profile", profileId: persisted.profileId };
    }
    return { mode: "active", profileId: null };
  });
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [running, setRunning] = useState(null);
  const [doc, setDoc] = useState(null);
  const [list, setList] = useState([]);
  const [ablationMode, setAblationMode] = useState(false);
  const [ablation, setAblation] = useState(null);     // { id }
  const [ablationDoc, setAblationDoc] = useState(null);
  // Research Window preset selector. Single-window presets fill the
  // start/end inputs; composite presets are blocked at submit time
  // (composite WF is not supported yet — see acceptance criteria).
  const [researchPresetId, setResearchPresetId] = useState(() => loadResearchPreset("walkforward", "custom"));

  // Persist form + picker on every change.
  useEffect(() => {
    const payload = { profileId: picker?.profileId || null };
    for (const k of PERSISTED_FORM_KEYS) payload[k] = form[k];
    _savePersisted(payload);
  }, [form, picker]);

  const refreshList = useCallback(async () => {
    try { setList((await api.walkforwardList()) || []); } catch {}
  }, []);
  useEffect(() => { refreshList(); }, [refreshList]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const list = await api.strategyProfiles();
        const active = await api.strategyActive();
        if (!alive) return;
        setProfiles(list || []);
        setActiveId(active?.active_strategy_id || null);
        // If the persisted profile id no longer exists, fallback to
        // the active strategy (or unset entirely) — never crash on
        // stale localStorage state.
        if (picker.profileId && !(list || []).find((p) => p.id === picker.profileId)) {
          if (active?.active_strategy_id) {
            setPicker({ mode: "profile", profileId: active.active_strategy_id });
          } else {
            setPicker({ mode: "active", profileId: null });
          }
        } else if (!picker.profileId && active?.active_strategy_id) {
          setPicker({ mode: "profile", profileId: active.active_strategy_id });
        }
      } catch {}
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const selectedProfile = useMemo(
    () => profiles.find((p) => p.id === (picker.profileId || activeId)),
    [profiles, picker, activeId],
  );

  // Auto-recover a still-running job after a refresh: the engine task
  // lives on the backend, so we just need to re-attach the poller.
  useEffect(() => {
    if (running?.id) return;
    const active = (list || []).find((r) => _isActiveStatus(r.status));
    if (active) {
      setRunning({ id: active.id });
      // Seed `doc` from the list payload so the progress card shows
      // immediately, without waiting for the first detail fetch.
      setDoc(active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  // Per-job polling. Slow tick (~10s) is preferred to preserve
  // bandwidth on long multi-fold runs — engine persists at every
  // phase transition (incl. mid-fold), so 10s already gives the
  // operator near-real-time visibility.
  useEffect(() => {
    if (!running?.id) return;
    let id; let stopped = false;
    const tick = async () => {
      try {
        const d = await api.walkforwardGet(running.id);
        if (stopped) return;
        setDoc(d);
        if (["completed", "cancelled", "failed"].includes(d.status)) {
          setRunning(null);
          refreshList();
        }
      } catch {}
    };
    tick();
    id = setInterval(tick, 10_000);
    return () => { stopped = true; clearInterval(id); };
  }, [running, refreshList]);

  // List polling while any job is active — keeps the table row up to
  // date (status badge, progress %) for jobs the operator hasn't
  // opened in detail yet. 15s cadence matches the spec.
  useEffect(() => {
    const anyActive = (list || []).some((r) => _isActiveStatus(r.status));
    if (!anyActive) return;
    // 20s list refresh during active runs — matches the operator's
    // research workflow (no need to see <20s-fresh aggregate stats).
    const id = setInterval(refreshList, 20_000);
    return () => clearInterval(id);
  }, [list, refreshList]);

  const submit = async () => {
    if (!picker.profileId) {
      toast.error("Pick a strategy profile first");
      return;
    }
    // Composite Walk-Forward is intentionally NOT implemented yet —
    // fold semantics across non-contiguous ranges add risk that we
    // chose not to take in this phase. Block at submit so the
    // operator can't accidentally trigger a flattened range.
    const _preset = findPreset(researchPresetId);
    if (_preset && _preset.type === "composite") {
      toast.error(
        "Composite windows are supported for Backtest first; " +
        "Walk-Forward composite will be added later. " +
        "Switch to a single-window preset or use Custom Date Range."
      );
      return;
    }
    try {
      const body = {
        symbol: form.symbol,
        start_iso: new Date(form.start_iso).toISOString(),
        end_iso: new Date(form.end_iso).toISOString(),
        n_folds: parseInt(form.n_folds, 10),
        initial_balance: parseFloat(form.initial_balance),
        leverage: parseInt(form.leverage, 10),
        order_size_mode: form.order_size_mode,
        order_size_value: parseFloat(form.order_size_value),
        hedge_mode: form.hedge_mode,
        fee_rate: parseFloat(form.fee_rate),
        slippage_bps: parseFloat(form.slippage_bps),
        liquidations_mode: form.liquidations_mode,
        strategy_id: picker.profileId,
      };
      if (ablationMode) {
        const r = await api.walkforwardAblationRun(body);
        setAblation({ id: r.id });
        setAblationDoc(null);
        setDoc(null);
        toast.success(`Ablation queued (${r.id.slice(0, 8)})`);
      } else {
        const r = await api.walkforwardRun(body);
        setRunning({ id: r.id });
        setDoc(null);
        toast.success(`Walk-forward queued (${r.id.slice(0, 8)})`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e.message;
      // 409 from /research-jobs/* means the slot is held. Offer the
      // operator a one-click force-cancel so they're never stuck.
      if (status === 409) {
        toast.error(detail, {
          action: {
            label: "Inspect / force cancel",
            onClick: async () => {
              try {
                const st = await api.researchJobsStatus();
                const linkedSummary = st?.linked
                  ? `linked ${st.linked.collection}/${(st.linked.id || "").slice(0, 8)} (status=${st.linked.status})`
                  : "no linked DB doc";
                if (!st?.slot) {
                  toast.success("No slot held — refresh and retry.");
                  refreshList();
                  return;
                }
                if (window.confirm(
                  `Force-cancel current research slot?\n\n` +
                  `kind=${st.slot.kind} slot=${st.slot.slot_id_short} ` +
                  `age=${Math.round(st.slot.age_seconds)}s\n` +
                  `${linkedSummary}\n` +
                  `${st.stale ? "STALE: " + st.stale_reason : ""}`
                )) {
                  const r = await api.researchJobsCancelCurrent();
                  toast.success(
                    r?.marked_failed?.length
                      ? `Slot released. Marked ${r.marked_failed.length} DB doc(s) failed.`
                      : "Slot released."
                  );
                  refreshList();
                }
              } catch (err) {
                toast.error(err?.response?.data?.detail || err.message);
              }
            },
          },
        });
        return;
      }
      toast.error(detail);
    }
  };

  // Ablation polling — slow tick (every 10s) to keep the UI in sync
  // without overwhelming the backend during the 6×WF cascade.
  useEffect(() => {
    if (!ablation?.id) return;
    let id; let stopped = false;
    const tick = async () => {
      try {
        const d = await api.walkforwardAblationGet(ablation.id);
        if (stopped) return;
        setAblationDoc(d);
        if (["completed", "failed", "cancelled"].includes(d.status)) {
          setAblation(null);
        }
      } catch {}
    };
    tick();
    // 15s ablation poll — gentler on the backend. Engine persists at
    // every variant phase transition so 15s still feels real-time.
    id = setInterval(tick, 15_000);
    return () => { stopped = true; clearInterval(id); };
  }, [ablation]);

  const cancel = async () => { if (running?.id) await api.walkforwardCancel(running.id); };
  const open = async (id) => { try { setDoc(await api.walkforwardGet(id)); } catch (e) { toast.error(e.message); } };
  const remove = async (id) => { await api.walkforwardDelete(id); refreshList(); if (doc?.id === id) setDoc(null); };

  // Reset every persisted form field back to DEFAULT_FORM and clear
  // the picker — useful when the operator wants to start clean. Also
  // wipes the localStorage entry so the next mount loads defaults.
  // Profile-derived fields (symbol always BTCUSDT, timeframe from the
  // selected profile) keep working unchanged.
  const resetDefaults = () => {
    setForm({
      ...DEFAULT_FORM,
      start_iso: defaultStart(),
      end_iso: defaultEnd(),
    });
    setPicker(activeId
      ? { mode: "profile", profileId: activeId }
      : { mode: "active", profileId: null });
    try { localStorage.removeItem(WF_FORM_STORAGE_KEY); } catch {}
    toast.success("Form reset to defaults");
  };

  // Resolve canonical progress fields (with backward-compat fallbacks
  // to the legacy keys `progress` / `current_fold` so older docs in
  // Mongo still render correctly).
  const progressPct = doc
    ? (doc.progress_percent != null ? doc.progress_percent : (doc.progress || 0))
    : 0;
  const currentFold = doc
    ? (doc.current_fold_index != null ? doc.current_fold_index : doc.current_fold)
    : null;
  const totalFolds = doc?.total_folds || form.n_folds;
  const stale = doc && _isActiveStatus(doc.status) ? _staleMinutes(doc.updated_at) : null;
  const isStale = stale != null && stale >= 2; // ≥ 2 min without engine persist

  return (
    <div className="p-6 space-y-6">
      <SectionTitle sub="Splits the date range into N equal chronological folds and runs the SAME Strategy Profile on each. Strong & consistent across folds = real edge. Strong only on one window = overfit risk.">
        Walk-Forward Validation
      </SectionTitle>

      {/* Strategy picker + summary */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-[#0F0F14] border border-border rounded-sm p-4">
          <StrategyPicker value={picker} onChange={setPicker} testidPrefix="wf-strat" />
        </div>
        <div className="lg:col-span-2">
          <StrategySummary profile={selectedProfile} />
        </div>
      </div>

      {/* Config form */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
        <div className="space-y-1.5">
          <Label className="text-xs">Symbol</Label>
          <Input value={form.symbol} disabled className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Timeframe</Label>
          <Input value={selectedProfile?.timeframe || "—"} disabled className="bg-[#1A1A22] border-border rounded-sm font-mono text-[#A0A0AB]" data-testid="wf-tf-display" />
          <span className="text-[10px] text-[#71717A]">From profile</span>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Number of folds</Label>
          <Select value={String(form.n_folds)} onValueChange={(v) => setForm({ ...form, n_folds: parseInt(v, 10) })}>
            <SelectTrigger data-testid="wf-folds" className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3">3 folds</SelectItem>
              <SelectItem value="5">5 folds</SelectItem>
              <SelectItem value="10">10 folds</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Liquidation simulation</Label>
          <Select value={form.liquidations_mode} onValueChange={(v) => setForm({ ...form, liquidations_mode: v })}>
            <SelectTrigger className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="skip">Skip (no historical liq data)</SelectItem>
              <SelectItem value="assume_pass">Assume PASS</SelectItem>
              <SelectItem value="assume_fail">Assume FAIL</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5 md:col-span-2">
          <Label className="text-xs">Research window</Label>
          <Select
            value={researchPresetId}
            onValueChange={(v) => {
              setResearchPresetId(v);
              persistResearchPreset("walkforward", v);
              const p = findPreset(v);
              if (!p || p.type !== "single" || !p.start_iso || !p.end_iso) {
                // composite / custom → leave manual dates intact
                if (p && p.type === "composite") {
                  toast(
                    "Composite windows are supported for Backtest first; " +
                    "Walk-Forward composite will be added later."
                  );
                }
                return;
              }
              const next_start = isoToInput(p.start_iso);
              const next_end   = isoToInput(p.end_iso);
              setForm((f) => ({ ...f, start_iso: next_start, end_iso: next_end }));
            }}
          >
            <SelectTrigger
              className="bg-[#1A1A22] border-border rounded-sm"
              data-testid="wf-research-window-select"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {RESEARCH_PRESETS.map((p) => (
                <SelectItem
                  key={p.id}
                  value={p.id}
                  data-testid={`wf-research-window-option-${p.id}`}
                >
                  {p.label}
                  {p.type === "composite" ? "  (composite — Backtest only)" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {(() => {
            const p = findPreset(researchPresetId);
            if (!p || p.type !== "composite") return null;
            return (
              <div
                data-testid="wf-research-window-composite-warning"
                className="text-[11px] text-[#F59E0B]"
              >
                Composite WF not supported yet — pick a single-window preset to run.
              </div>
            );
          })()}
          <div className="pt-1 flex flex-wrap items-center gap-2">
            <ResearchCachePill
              presetId={researchPresetId}
              strategyProfileId={picker.profileId}
              symbol={form.symbol}
              testidPrefix="wf"
            />
            <WarmAllPresetsButton
              strategyProfileId={picker.profileId}
              symbol={form.symbol}
              testidPrefix="wf"
            />
            <CacheTelemetryBadge testidPrefix="wf" />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Start (UTC)</Label>
          <Input data-testid="wf-start" type="datetime-local" value={form.start_iso} onChange={(e) => setForm({ ...form, start_iso: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">End (UTC)</Label>
          <Input data-testid="wf-end" type="datetime-local" value={form.end_iso} onChange={(e) => setForm({ ...form, end_iso: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Initial balance (USDT)</Label>
          <Input data-testid="wf-initial-balance" type="number" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Leverage</Label>
          <Input data-testid="wf-leverage" type="number" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Order size mode</Label>
          <Select value={form.order_size_mode} onValueChange={(v) => setForm({ ...form, order_size_mode: v })}>
            <SelectTrigger data-testid="wf-order-mode" className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="usdt">Fixed USDT</SelectItem><SelectItem value="percent">% of balance</SelectItem></SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Order size value</Label>
          <Input data-testid="wf-order-value" type="number" value={form.order_size_value} onChange={(e) => setForm({ ...form, order_size_value: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Fee rate</Label>
          <Input data-testid="wf-fee-rate" type="number" step="0.0001" value={form.fee_rate} onChange={(e) => setForm({ ...form, fee_rate: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Slippage (bps)</Label>
          <Input data-testid="wf-slippage" type="number" value={form.slippage_bps} onChange={(e) => setForm({ ...form, slippage_bps: e.target.value })} className="bg-[#1A1A22] border-border rounded-sm font-mono" />
        </div>

        <div className="md:col-span-3 lg:col-span-4 flex items-center justify-between pt-4 border-t border-border">
          <div className="flex items-center gap-3 flex-wrap">
            <Switch data-testid="wf-hedge" checked={form.hedge_mode} onCheckedChange={(v) => setForm({ ...form, hedge_mode: v })} />
            <Label className="text-xs">Hedge mode</Label>
            {form.hedge_mode ? (
              <span
                data-testid="wf-hedge-lbank-warning"
                className="text-[10px] text-[#F59E0B] font-mono leading-tight max-w-[460px]"
              >
                Hedge mode is not supported on LBank one-way mode. Results
                with Hedge ON and max_open_positions ≥ 2 may not be
                replayable live.
              </span>
            ) : null}
            <span className="mx-2 text-[#27272A]">·</span>
            <Switch
              data-testid="wf-ablation-toggle"
              checked={ablationMode}
              onCheckedChange={(v) => setAblationMode(!!v)}
              disabled={!!running || !!ablation}
            />
            <Label className="text-xs">
              Run setup ablation
              <span className="block text-[10px] text-[#71717A] font-mono font-normal">
                Runs FULL + N child WFs (one per enabled setup removed). Research only.
              </span>
            </Label>
          </div>
          <div className="flex gap-2">
            <Button
              data-testid="wf-reset-defaults"
              variant="outline"
              onClick={resetDefaults}
              disabled={!!running}
              className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] disabled:opacity-40"
              title="Reset all Walk-Forward form fields and clear saved settings"
            >
              Reset to defaults
            </Button>
            {running ? (
              <Button data-testid="wf-cancel" variant="outline" onClick={cancel} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]">
                <X size={14} strokeWidth={1.5} className="mr-1.5" /> Cancel
              </Button>
            ) : (
              <Button data-testid="wf-run" onClick={submit} disabled={!picker.profileId} className="bg-[#00E676] text-black hover:bg-[#00C865] rounded-sm disabled:opacity-40 disabled:cursor-not-allowed">
                <Play size={14} strokeWidth={2} className="mr-1.5" /> Run Walk-Forward
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Active progress */}
      {running ? (
        <div
          className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-3"
          data-testid="wf-running-card"
        >
          <div className="flex items-center justify-between text-sm">
            <div className="flex flex-col gap-0.5">
              <span className="text-[#A0A0AB]">
                Running fold{" "}
                <span data-testid="wf-running-fold" className="font-mono text-white">
                  {currentFold || "?"}
                </span>
                {" "}of{" "}
                <span data-testid="wf-running-total-folds" className="font-mono text-white">
                  {totalFolds || "?"}
                </span>
              </span>
              {doc?.current_phase ? (
                <span
                  data-testid="wf-running-phase"
                  className="text-[10px] uppercase tracking-widest text-[#71717A] font-mono"
                >
                  phase: <span className="text-[#A0A0AB]">{doc.current_phase}</span>
                </span>
              ) : null}
            </div>
            <span data-testid="wf-running-progress" className="font-mono">
              {Number(progressPct || 0).toFixed(1)}%
            </span>
          </div>
          <Progress value={progressPct} className="h-1.5" />
          {isStale ? (
            <div
              data-testid="wf-running-stale"
              className="text-[11px] text-[#F59E0B] font-mono flex items-center gap-2"
            >
              <AlertTriangle size={12} strokeWidth={1.5} />
              <span>
                Possibly stalled — last update {stale.toFixed(1)} min ago.
                The job is still queued on the server; refresh to re-check
                or cancel manually if needed.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Ablation progress + results */}
      {(ablation || ablationDoc) ? (
        <AblationCard doc={ablationDoc} active={!!ablation} />
      ) : null}

      {/* Results */}
      {doc && doc.status === "completed" ? <WfResults doc={doc} /> : null}
      {doc && doc.status === "failed" ? (
        <div
          data-testid="wf-failed-banner"
          className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] rounded-sm p-4 text-sm space-y-2"
        >
          <div className="font-semibold uppercase tracking-widest text-[10px]">
            Walk-forward failed
          </div>
          <div className="font-mono text-xs break-all" data-testid="wf-failed-message">
            {doc.error && String(doc.error).trim()
              ? doc.error
              : "(backend reported failure with no error message — see traceback below)"}
          </div>
          {(doc.failed_phase || doc.failed_fold_index != null) ? (
            <div className="font-mono text-[10px] text-[#FF7A6E]/80">
              {doc.failed_phase ? <>phase: <span className="text-[#FF3B30]">{doc.failed_phase}</span> · </> : null}
              {doc.failed_fold_index != null ? <>fold: <span className="text-[#FF3B30]">{doc.failed_fold_index}</span> · </> : null}
              {doc.id ? <>run_id: <span className="text-[#A0A0AB]">{doc.id}</span></> : null}
            </div>
          ) : null}
          {doc.failed_profile_meta ? (
            <div className="font-mono text-[10px] text-[#A0A0AB]">
              profile: {doc.failed_profile_meta.profile_name || "—"}
              {" "}({doc.failed_profile_meta.profile_id || "—"}) ·
              {" "}tf: {doc.failed_profile_meta.profile_timeframe || "—"} ·
              {" "}groups: {doc.failed_profile_meta.n_condition_groups ?? "—"} ·
              {" "}flat conds: {doc.failed_profile_meta.n_flat_conditions ?? "—"}
            </div>
          ) : null}
          {doc.failed_kline_meta ? (
            <div className="font-mono text-[10px] text-[#FFB020]"
                 data-testid="wf-failed-kline-meta">
              kline-fetch: provider={doc.failed_kline_meta.provider} ·
              {" "}symbol={doc.failed_kline_meta.symbol} ·
              {" "}tf={doc.failed_kline_meta.timeframe} ·
              {" "}attempts={doc.failed_kline_meta.attempts} ·
              {" "}window=[{doc.failed_kline_meta.start_ms}, {doc.failed_kline_meta.end_ms}] ·
              {" "}timeouts(connect={doc.failed_kline_meta.connect_timeout_s}s, read={doc.failed_kline_meta.read_timeout_s}s)
              <div className="mt-0.5 text-[#FF6F00]">
                underlying: {doc.failed_kline_meta.underlying_error}
              </div>
            </div>
          ) : null}
          {doc.failed_traceback_tail ? (
            <details className="font-mono text-[10px] text-[#A0A0AB]">
              <summary className="cursor-pointer hover:text-white">traceback (last frames)</summary>
              <pre className="whitespace-pre-wrap mt-2 p-2 bg-black/30 rounded-sm">{doc.failed_traceback_tail}</pre>
            </details>
          ) : null}
        </div>
      ) : null}

      {/* History */}
      <div className="bg-[#0F0F14] border border-border rounded-sm">
        <div className="px-4 py-3 border-b border-border text-xs uppercase tracking-widest text-[#A0A0AB]">Recent Runs</div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="wf-list">
            <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
              <tr>
                <th className="text-left px-4 py-2">Created</th>
                <th className="text-left px-4 py-2">TF</th>
                <th className="text-left px-4 py-2">Range</th>
                <th className="text-right px-4 py-2">Folds</th>
                <th className="text-left px-4 py-2">Consistency</th>
                <th className="text-right px-4 py-2">Sum PnL</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-6 text-center text-[#71717A]">No walk-forward runs yet.</td></tr>
              ) : list.map((b) => {
                const rowProgress = b.progress_percent != null ? b.progress_percent : (b.progress || 0);
                const rowFold = b.current_fold_index != null ? b.current_fold_index : b.current_fold;
                const rowTotal = b.total_folds || b.config?.n_folds;
                const rowStale = _isActiveStatus(b.status) ? _staleMinutes(b.updated_at) : null;
                const rowIsStale = rowStale != null && rowStale >= 2;
                return (
                  <tr key={b.id} className="border-t border-border/60" data-testid={`wf-row-${b.id}`}>
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{fmtTime(b.created_at)}</td>
                    <td className="px-4 py-2 font-mono">{b.config?.timeframe}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{b.config?.start_iso?.slice(0, 16)} → {b.config?.end_iso?.slice(0, 16)}</td>
                    <td className="px-4 py-2 cell-num">{b.config?.n_folds}</td>
                    <td className="px-4 py-2">{b.consistency ? <Badge kind={labelKind(b.consistency.label)}>{b.consistency.label}</Badge> : "—"}</td>
                    <td className={`px-4 py-2 cell-num ${(b.aggregate?.sum_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"}`}>
                      {b.aggregate ? `${b.aggregate.sum_pnl_usdt >= 0 ? "+" : ""}$${fmtUsd(b.aggregate.sum_pnl_usdt)}` : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex flex-col gap-0.5">
                        <Badge kind={b.status === "completed" ? "on" : b.status === "failed" ? "warn" : b.status === "cancelled" ? "off" : "neutral"}>{b.status}</Badge>
                        {_isActiveStatus(b.status) ? (
                          <span className="text-[10px] font-mono text-[#A0A0AB]"
                                data-testid={`wf-row-progress-${b.id}`}>
                            {rowFold ? `fold ${rowFold}/${rowTotal || "?"} · ` : ""}
                            {Number(rowProgress || 0).toFixed(0)}%
                          </span>
                        ) : null}
                        {rowIsStale ? (
                          <span
                            className="text-[10px] font-mono text-[#F59E0B]"
                            data-testid={`wf-row-stale-${b.id}`}
                          >
                            stalled? {rowStale.toFixed(1)}m
                          </span>
                        ) : null}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button size="sm" variant="outline" onClick={() => open(b.id)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] mr-2">Open</Button>
                      <Button size="sm" variant="outline" onClick={() => remove(b.id)} className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633]"><Trash2 size={14} strokeWidth={1.5} /></Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Compact USD formatter — matches Backtest.jsx::fmtEquityTick. Used in the
// MiniEquity sparkline tooltip so hovering shows a readable amount instead
// of the raw float (which is meaningless on tight ranges, e.g. equity at
// $9 997.78035431… vs $10 027.17558…).
const fmtMiniEquity = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(2)}k`;
  return `$${n.toFixed(2)}`;
};

function MiniEquity({ data }) {
  // Sanitize the input series before handing it to Recharts:
  //   * drop entries with non-finite equity (NaN / Infinity) — Recharts
  //     fails to compute the data domain and renders nothing when even
  //     one sample is non-finite.
  //   * keep the same point shape as before so the rest of the cell
  //     renders identically when data is healthy.
  // No calculation logic touched — pure render-side guard.
  const points = useMemo(() => {
    const raw = Array.isArray(data) ? data : [];
    const out = [];
    for (let i = 0; i < raw.length; i += 1) {
      const p = raw[i];
      if (!p) continue;
      const eq = Number(p.equity);
      if (!Number.isFinite(eq)) continue;
      out.push({ idx: out.length, equity: eq });
    }
    return out;
  }, [data]);
  if (points.length === 0) return <div className="text-xs text-[#71717A]">—</div>;
  return (
    <ResponsiveContainer width="100%" height={60}>
      <RLineChart data={points} margin={{ top: 4, right: 4, left: 4, bottom: 4 }}>
        <RTooltip
          contentStyle={{ background: "#0F0F14", border: "1px solid #27272A", borderRadius: 2, fontSize: 11, padding: "4px 8px" }}
          labelStyle={{ display: "none" }}
          formatter={(v) => [fmtMiniEquity(v), "Equity"]}
          cursor={{ stroke: "#27272A", strokeWidth: 1 }}
        />
        <Line type="monotone" dataKey="equity" stroke="#00E676" strokeWidth={1.2} dot={false} isAnimationActive={false} />
      </RLineChart>
    </ResponsiveContainer>
  );
}

function WfResults({ doc }) {
  const c = doc.consistency || {};
  const a = doc.aggregate || {};
  const folds = doc.folds || [];
  const strategyWarnings = doc.warnings || [];

  return (
    <div className="space-y-4">
      {doc.strategy_config_snapshot ? (
        <StrategySummary profile={doc.strategy_config_snapshot} />
      ) : null}

      {strategyWarnings.length > 0 ? (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] rounded-sm p-4 text-sm flex gap-3">
          <AlertTriangle size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold uppercase tracking-wider text-xs mb-1">Run Warnings</div>
            <ul className="list-disc ml-4 space-y-0.5">
              {strategyWarnings.map((w, i) => <li key={i}>{w}</li>)}
            </ul>
          </div>
        </div>
      ) : null}

      {/* Aggregate + score */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-[#0F0F14] border border-border rounded-sm p-4 col-span-2">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Consistency Score</div>
          <div className="mt-2 flex items-center gap-3">
            <Badge kind={labelKind(c.label)} testid="wf-score-badge">{c.label || "—"}</Badge>
            <span className="text-xs text-[#A0A0AB]">profitable folds: {(c.profitable_pct ?? 0).toFixed(0)}%</span>
          </div>
          <div className="text-xs font-mono text-[#A0A0AB] mt-2">
            return CV: {(c.return_cv ?? 0).toFixed(2)} · DD concentration: {(c.dd_concentration ?? 0).toFixed(2)}× median
          </div>
        </div>
        <StatCard label="Sum PnL" value={`${(a.sum_pnl_usdt || 0) >= 0 ? "+" : ""}$${fmtUsd(a.sum_pnl_usdt || 0)}`} accent={(a.sum_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"} />
        <StatCard label="Avg Win Rate" value={fmtPct(a.avg_win_rate || 0, 1)} sublabel={`${a.wins || 0}W / ${a.losses || 0}L`} />
        <StatCard label="Avg Profit Factor" value={Number(a.avg_profit_factor || 0).toFixed(2)} />
        <StatCard label="Worst Fold MaxDD" value={`-${fmtPct(a.worst_fold_max_drawdown_percent || 0, 2).replace("+", "")}`} accent="short-text" sublabel={`${a.valid_folds || 0}/${a.folds || 0} valid`} />
      </div>

      {/* Warnings */}
      {(c.warnings || []).length > 0 ? (
        <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 text-[#F59E0B] rounded-sm p-4 text-sm flex gap-3" data-testid="wf-warnings">
          <AlertTriangle size={18} strokeWidth={1.5} className="shrink-0 mt-0.5" />
          <ul className="list-disc ml-4 space-y-0.5">
            {c.warnings.map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      ) : null}

      {/* Per-fold table */}
      <div className="bg-[#0F0F14] border border-border rounded-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="wf-folds-table">
          <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
            <tr>
              <th className="text-left px-4 py-3">#</th>
              <th className="text-left px-4 py-3">Range</th>
              <th className="text-right px-4 py-3">Trades</th>
              <th className="text-right px-4 py-3">L/S</th>
              <th className="text-right px-4 py-3">PnL</th>
              <th className="text-right px-4 py-3">PnL %</th>
              <th className="text-right px-4 py-3">Win %</th>
              <th className="text-right px-4 py-3">PF</th>
              <th className="text-right px-4 py-3">Max DD</th>
              <th className="text-right px-4 py-3">Best</th>
              <th className="text-right px-4 py-3">Worst</th>
              <th className="text-left px-4 py-3 w-40">Equity</th>
            </tr>
          </thead>
          <tbody>
            {folds.map((f) => {
              if (f.skipped) {
                return (
                  <tr key={f.index} className="border-t border-border/60">
                    <td className="px-4 py-2 font-mono">{f.index}</td>
                    <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{f.start_iso?.slice(0, 16)} → {f.end_iso?.slice(0, 16)}</td>
                    <td colSpan={10} className="px-4 py-2 text-xs text-[#F59E0B]">SKIPPED — {f.skip_reason}</td>
                  </tr>
                );
              }
              const m = f.metrics || {};
              return (
                <tr key={f.index} className="border-t border-border/60">
                  <td className="px-4 py-2 font-mono">{f.index}</td>
                  <td className="px-4 py-2 font-mono text-xs text-[#A0A0AB]">{f.start_iso?.slice(0, 16)} → {f.end_iso?.slice(0, 16)}</td>
                  <td className="px-4 py-2 cell-num">{m.total_trades ?? 0}</td>
                  <td className="px-4 py-2 cell-num">{m.long_trades || 0}/{m.short_trades || 0}</td>
                  <td className={`px-4 py-2 cell-num ${(m.total_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"}`}>
                    {(m.total_pnl_usdt || 0) >= 0 ? "+" : ""}${fmtUsd(m.total_pnl_usdt || 0)}
                  </td>
                  <td className={`px-4 py-2 cell-num ${(m.total_pnl_percent || 0) >= 0 ? "long-text" : "short-text"}`}>{fmtPct(m.total_pnl_percent || 0)}</td>
                  <td className="px-4 py-2 cell-num">{fmtPct(m.win_rate || 0, 1)}</td>
                  <td className="px-4 py-2 cell-num">{Number(m.profit_factor || 0).toFixed(2)}</td>
                  <td className="px-4 py-2 cell-num short-text">-{fmtPct(m.max_drawdown_percent || 0, 2).replace("+", "")}</td>
                  <td className="px-4 py-2 cell-num long-text">${fmtUsd(m.best_trade_usdt || 0)}</td>
                  <td className="px-4 py-2 cell-num short-text">${fmtUsd(m.worst_trade_usdt || 0)}</td>
                  <td className="px-4 py-2"><MiniEquity data={f.equity_curve} /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}



// ------------------------------------------------------------------
// Ablation results card.
// Renders the comparison table of all variants (FULL + one per
// removed setup) plus inline progress while the ablation is still
// running. Auto-highlights the best variant by consistency label
// rank, breaking ties on total PnL.
// ------------------------------------------------------------------
const _CONSISTENCY_RANK = { Strong: 3, Moderate: 2, Weak: 1, "Overfit Risk": 0 };

function _rankBest(variants) {
  let best = null;
  for (const v of variants || []) {
    if (v.status !== "completed") continue;
    const s = v.summary || {};
    const rank = _CONSISTENCY_RANK[s.consistency_label] ?? -1;
    const pnl = Number(s.total_pnl_usdt || 0);
    if (!best) { best = { v, rank, pnl }; continue; }
    if (rank > best.rank || (rank === best.rank && pnl > best.pnl)) {
      best = { v, rank, pnl };
    }
  }
  return best?.v?.variant_name || null;
}

function _fmtOrDash(v, fmt = (x) => Number(x).toFixed(2)) {
  if (v === null || v === undefined || Number.isNaN(v)) return "—";
  try { return fmt(v); } catch { return "—"; }
}

// Format ms as "Xm Ys" or "Ys" for compact ETA display. Returns "—"
// on missing/invalid input so the UI never renders NaN/Infinity.
function _fmtDuration(ms) {
  if (ms == null || Number.isNaN(Number(ms)) || !Number.isFinite(Number(ms))) return "—";
  const total = Math.max(0, Math.round(Number(ms) / 1000));
  if (total < 60) return `${total}s`;
  const m = Math.floor(total / 60);
  const s = total % 60;
  if (m < 60) return `${m}m ${s}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

function AblationCard({ doc, active }) {
  const variants = doc?.variants || [];
  const bestName = _rankBest(variants);
  const totalV = doc?.total_variants;
  const completedV = doc?.completed_variants ?? 0;
  const currName = doc?.current_variant_name;
  const status = doc?.status || (active ? "pending" : "—");
  const isActive = status === "pending" || status === "running";
  const pct = totalV ? Math.min(100, (completedV / totalV) * 100) : 0;

  // Stale detection — defensive against missing `updated_at` (older
  // ablation docs predate this field).
  let staleMin = null;
  if (isActive && doc?.updated_at) {
    const t = Date.parse(doc.updated_at);
    if (Number.isFinite(t)) {
      const delta = Date.now() - t;
      staleMin = delta < 0 ? 0 : delta / 60_000;
    }
  }
  const isStale = staleMin != null && staleMin >= 2;

  return (
    <div
      data-testid="wf-ablation-card"
      className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4"
    >
      <div className="flex items-center justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-[#A0A0AB]">
            Setup Ablation
          </div>
          <div className="text-[10px] font-mono text-[#71717A] mt-0.5">
            Full profile vs one variant per setup removed · research only · saved profile NOT modified
          </div>
        </div>
        <div className="text-right">
          <Badge kind={status === "completed" ? "on"
                     : status === "failed" ? "warn"
                     : status === "cancelled" ? "off" : "neutral"}>
            {status}
          </Badge>
          {totalV ? (
            <div className="text-[10px] font-mono text-[#A0A0AB] mt-1"
                 data-testid="wf-ablation-progress">
              {completedV}/{totalV} variants
              {currName && status === "running" ? ` · current: ${currName}` : ""}
            </div>
          ) : null}
        </div>
      </div>

      {/* Global progress bar + timing — visible during active runs and
          remains as a final snapshot after completion. */}
      {totalV ? (
        <div className="space-y-1.5" data-testid="wf-ablation-progress-bar-wrap">
          <Progress value={pct} className="h-1.5" />
          <div className="flex items-center justify-between text-[10px] font-mono text-[#71717A]">
            <span data-testid="wf-ablation-elapsed">
              elapsed: <span className="text-[#A0A0AB]">{_fmtDuration(doc?.elapsed_ms)}</span>
            </span>
            <span>
              avg/variant: <span className="text-[#A0A0AB]">{_fmtDuration(doc?.avg_variant_runtime_ms)}</span>
            </span>
            <span data-testid="wf-ablation-eta">
              {isActive
                ? <>eta: <span className="text-[#A0A0AB]">{_fmtDuration(doc?.estimated_remaining_ms)}</span></>
                : <>total: <span className="text-[#A0A0AB]">{_fmtDuration(doc?.total_runtime_ms ?? doc?.elapsed_ms)}</span></>}
            </span>
          </div>
          {isStale ? (
            <div
              data-testid="wf-ablation-stale"
              className="text-[10px] text-[#F59E0B] font-mono flex items-center gap-2 pt-1"
            >
              <AlertTriangle size={12} strokeWidth={1.5} />
              <span>
                Possibly stalled — last update {staleMin.toFixed(1)} min ago.
                The job is still queued on the server; refresh to re-check.
              </span>
            </div>
          ) : null}
        </div>
      ) : null}

      {doc?.error ? (
        <div className="bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] rounded-sm p-3 text-xs font-mono"
             data-testid="wf-ablation-error">
          {doc.error}
        </div>
      ) : null}

      <div className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="wf-ablation-table">
          <thead className="text-[10px] uppercase tracking-widest text-[#71717A]">
            <tr>
              <th className="text-left px-3 py-2">Variant</th>
              <th className="text-left px-3 py-2">Removed Setup</th>
              <th className="text-right px-3 py-2">Trades</th>
              <th className="text-right px-3 py-2">PnL (USDT)</th>
              <th className="text-right px-3 py-2">PF</th>
              <th className="text-right px-3 py-2">Win %</th>
              <th className="text-right px-3 py-2">Profitable Folds %</th>
              <th className="text-right px-3 py-2">CV</th>
              <th className="text-right px-3 py-2">Worst Fold Max DD</th>
              <th className="text-left px-3 py-2">Score</th>
            </tr>
          </thead>
          <tbody>
            {variants.length === 0 ? (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-[#71717A] text-xs">
                  Variants will appear here as they complete.
                </td>
              </tr>
            ) : variants.map((v) => {
              const s = v.summary || {};
              const isBest = bestName && v.variant_name === bestName && v.status === "completed";
              const rowCls = isBest
                ? "border-t border-[#00E676]/30 bg-[#00E676]/5"
                : "border-t border-border/60";
              return (
                <tr key={v.variant_name + (v.removed_setup_id || "full")}
                    className={rowCls}
                    data-testid={`wf-ablation-row-${v.variant_name}`}>
                  <td className="px-3 py-2 font-mono text-xs">
                    {v.variant_name}{isBest ? " ★" : ""}
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-[#A0A0AB]">
                    {v.removed_setup_name
                      ? <>{v.removed_setup_name}{v.removed_setup_timeframe ? ` · ${v.removed_setup_timeframe}` : ""}</>
                      : "—"}
                  </td>
                  {v.status !== "completed" ? (
                    <td colSpan={8} className="px-3 py-2 text-xs">
                      <Badge kind={v.status === "failed" ? "warn" : "neutral"}>
                        {v.status}
                      </Badge>
                      {v.error ? (
                        <span className="ml-2 font-mono text-[10px] text-[#FF7A6E]">
                          {v.error}
                        </span>
                      ) : null}
                    </td>
                  ) : (
                    <>
                      <td className="px-3 py-2 cell-num">{s.total_trades ?? "—"}</td>
                      <td className={`px-3 py-2 cell-num ${(s.total_pnl_usdt || 0) >= 0 ? "long-text" : "short-text"}`}>
                        {s.total_pnl_usdt != null
                          ? `${s.total_pnl_usdt >= 0 ? "+" : ""}$${fmtUsd(s.total_pnl_usdt)}`
                          : "—"}
                      </td>
                      <td className="px-3 py-2 cell-num">{_fmtOrDash(s.avg_profit_factor)}</td>
                      <td className="px-3 py-2 cell-num">{_fmtOrDash(s.avg_win_rate, (x) => `${Number(x).toFixed(1)}%`)}</td>
                      <td className="px-3 py-2 cell-num">{_fmtOrDash(s.profitable_folds_percent, (x) => `${Number(x).toFixed(0)}%`)}</td>
                      <td className="px-3 py-2 cell-num">{_fmtOrDash(s.return_cv)}</td>
                      <td className="px-3 py-2 cell-num short-text">
                        {s.worst_fold_max_dd_percent != null
                          ? `-${Number(s.worst_fold_max_dd_percent).toFixed(2)}%`
                          : "—"}
                      </td>
                      <td className="px-3 py-2">
                        <Badge kind={labelKind(s.consistency_label)}>
                          {s.consistency_label || "—"}
                        </Badge>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {bestName ? (
        <div className="text-[10px] font-mono text-[#A0A0AB]"
             data-testid="wf-ablation-best-hint">
          ★ Best variant by consistency / PnL: <span className="text-[#00E676]">{bestName}</span>
        </div>
      ) : null}
    </div>
  );
}
